import { useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Diamond, Package, Users } from 'lucide-react';
import { loadHomeSummary } from '../lib/homeSummary';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/pipeline/ui';
import { SourceBadge } from '../components/pipeline/charts';

// Home — what you have built so far, and what backs it.
//
// Ported from the mock in Novamund-proto\. Every figure comes from get_home_summary, which returns
// values the app already stored. Nothing on this page is computed from a guess.
//
// TWO PANELS FROM THE DESIGN ARE ABSENT ON PURPOSE, and their absence is the point:
//   Dataset usage - market_grounding_cache never records WHICH dataset answered a query, so there
//   is nothing to chart. CLAUDE.md records what it would take to start recording it.
//   Revenue - wallet_ledger holds grants, and with no payment adapter shipped none of them is money
//   received. A revenue tile would be a number nobody measured.
// Neither is drawn as an empty box or a "coming soon". A placeholder for a metric that does not
// exist still teaches the reader that it does.

function Card({ title, hint, children, className = '', testId }) {
  return (
    <section className={`rounded-lg border border-border bg-card p-5 ${className}`} data-testid={testId}>
      {title && <h2 className="font-heading text-base font-semibold">{title}</h2>}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      {children}
    </section>
  );
}

function KpiTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="mt-2 font-data text-3xl font-bold">{value}</div>
    </div>
  );
}

// The grounding split. Segments are sized against the TOTAL, not against the labelled subset, so a
// run of unlabelled ideas shows up as a gap instead of quietly inflating the grounded share.
//
// Unlabelled ideas predate the labelling and carry no claim either way. They are drawn as an
// em-dash segment rather than being folded into "modeled-not-fact" — that would be asserting
// something about work nobody checked — and rather than getting invented copy, since the mock's
// ledger lists no key for this state. A proper key is worth adding; it is not mine to invent.
function GroundingBar({ g, testId }) {
  const total = (g?.corpus_grounded ?? 0) + (g?.modeled_not_fact ?? 0) + (g?.unlabelled ?? 0);
  if (!total) return null;
  // Rounding each share on its own produced 88% + 13% = 101% on real data. The last drawn
  // segment takes the remainder instead, so the parts always add up to the whole.
  const raw = (n) => Math.round((n / total) * 100);
  const seg = [
    { n: g.corpus_grounded, cls: 'bg-success', label: 'corpus-grounded' },
    { n: g.modeled_not_fact, cls: 'bg-warning', label: 'modeled-not-fact' },
    { n: g.unlabelled, cls: 'bg-muted-foreground/40', label: null },
  ].filter((s) => s.n > 0).map((s, i, all) => ({
    ...s,
    pct: i === all.length - 1
      ? 100 - all.slice(0, i).reduce((sum, x) => sum + raw(x.n), 0)
      : raw(s.n),
  }));

  return (
    <div data-testid={testId}>
      <div className="mt-4 flex h-8 gap-0.5 overflow-hidden rounded-lg" dir="ltr">
        {seg.map((s) => (
          <div
            key={s.label ?? 'unlabelled'}
            className={`flex items-center justify-center font-data text-xs font-bold text-white ${s.cls}`}
            style={{ width: `${s.pct}%` }}
          >
            {s.pct}%
          </div>
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        {seg.map((s) => (
          <span key={s.label ?? 'unlabelled'} className="flex items-center gap-1.5 text-xs">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${s.cls}`} />
            {s.label ? <SourceBadge source={s.label} /> : <span className="text-muted-foreground">— {s.n}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    loadHomeSummary().then(setData).catch(() => setFailed(true));
  }, []);

  if (failed) {
    return (
      <>
        <h1 className="font-heading text-3xl font-bold"><T k="home.title" /></h1>
        <p className="mt-1 text-muted-foreground"><T k="common.loadFailed" /></p>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Skeleton className="h-9 w-64" />
        <Skeleton className="mt-6 h-72 w-full" />
      </>
    );
  }

  // Only ideas that have actually been scored can sit on a score axis. An unscored idea is not a
  // zero, and plotting it at zero would invent a bad result for work that simply has not run.
  const scored = (data.ideas ?? []).filter((i) => typeof i.score === 'number');
  const trend = scored.map((i) => ({ name: i.name || '—', value: i.score }));
  const top = [...scored].sort((a, b) => b.score - a.score).slice(0, 3);
  const hub = data.hub;

  // Day one: zeros in every tile are discouraging and say nothing. One next action instead.
  if (!(data.ideas ?? []).length) {
    return (
      <>
        <h1 className="font-heading text-3xl font-bold"><T k="home.title" /></h1>
        <p className="mt-1 text-muted-foreground"><T k="home.subtitle" /></p>
        <Card className="mt-6 py-14 text-center" testId="home-empty">
          <Diamond className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 font-heading text-lg font-semibold"><T k="home.emptyTitle" /></h2>
          <p className="mb-5 mt-1 text-sm text-muted-foreground"><T k="dashboard.subtitle" /></p>
          <Button onClick={() => navigate('/projects')} data-testid="home-empty-cta">
            {t('dashboard.newProject')}
          </Button>
        </Card>
      </>
    );
  }

  return (
    <>
      <h1 className="font-heading text-3xl font-bold"><T k="home.title" /></h1>
      <p className="mt-1 text-muted-foreground"><T k="home.subtitle" /></p>

      {hub && (
        <section className="mt-7" data-testid="home-hub">
          <div className="mb-3.5 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-semibold"><T k="home.hubTitle" /></h2>
              <span className="text-xs text-muted-foreground"><T k="hub.since30" /></span>
            </div>
            <span className="rounded bg-muted px-2 py-0.5 font-data text-[11px] text-muted-foreground">
              admin only
            </span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiTile icon={Diamond} label={<T k="hub.kpiIdeas" />} value={hub.ideas_30d} />
            <KpiTile icon={Package} label={<T k="hub.kpiPacks" />} value={hub.packs_30d} />
            <KpiTile icon={Users} label={<T k="hub.kpiActive" />} value={hub.active_users} />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Card title={t('hub.funnelTitle')} hint={t('hub.funnelHint')} testId="home-funnel">
              <div className="mt-4 space-y-2.5">
                {[
                  [t('hub.fMembers'), hub.funnel.members],
                  [t('hub.fIdeas'), hub.funnel.ideas],
                  [t('hub.fAnalyses'), hub.funnel.analyses],
                  [t('hub.fPacks'), hub.funnel.packs],
                ].map(([label, n]) => {
                  const max = Math.max(1, ...Object.values(hub.funnel));
                  return (
                    <div key={label} className="flex items-center gap-3">
                      <span className="w-28 shrink-0 truncate text-xs text-muted-foreground">{label}</span>
                      <span className="h-4 rounded-e bg-foreground/80" style={{ width: `${(n / max) * 70}%` }} />
                      <span className="font-data text-xs text-muted-foreground">{n}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title={t('hub.groundingTitle')} hint={t('hub.groundingHint')} testId="home-hub-grounding">
              <GroundingBar g={hub.grounding} testId="hub-grounding-bar" />
            </Card>
          </div>
        </section>
      )}

      <Card
        className="mt-7"
        title={t('home.scoreTrend')}
        hint={t('home.scoreTrendHint')}
        testId="home-score-trend"
      >
        {trend.length ? (
          <div className="mt-4 w-full min-w-0">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trend} margin={{ top: 8, left: 8, right: 24, bottom: 30 }}>
                <CartesianGrid stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title={t('home.bestIdea')} testId="home-best-ideas">
          <div className="mt-3">
            {top.map((i, n) => (
              <div
                key={i.project_id}
                className={`flex items-center gap-3.5 py-3 ${n < top.length - 1 ? 'border-b border-border' : ''}`}
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg font-data text-xs font-bold
                    ${n === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  {n + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold" title={i.name || ''}>
                  {i.name || '—'}
                </span>
                <span className="font-data text-xl font-extrabold">{i.score}</span>
                {/* Read from the artifact, per row. An idea with no recorded label gets no badge —
                    never a defaulted one, which would be a claim we cannot support. */}
                {i.score_label ? <SourceBadge source={i.score_label} /> : null}
              </div>
            ))}
          </div>
        </Card>

        <Card title={t('home.grounding')} hint={t('home.groundingHint')} testId="home-grounding">
          <GroundingBar g={data.grounding} testId="home-grounding-bar" />
        </Card>
      </div>
    </>
  );
}
